import { Activity, Landmark, Users } from 'lucide-react';
import { useMemo } from 'react';
import {
  CLASSIFIABLE_SECTOR_TAGS,
  changeStageFilter,
  removeStageChip,
  stageFilterChipStages,
  stageFilterWants,
  type SectorTag,
  type Stage,
} from '@nemo/core/model';
import {
  ALL_CANDIDATE_TYPES,
  CANDIDATE_TYPE_OPTIONS,
  CONTACT_OPTIONS,
  KNOW_ANYONE_OPTIONS,
  SOURCE_OPTIONS,
  countryName,
  flagEmoji,
} from '../dealroom/format.js';
import type { CandidateCountry } from '../dealroom/types.js';
import {
  CANDIDATE_REGION_FILTERS,
  DEFAULT_CANDIDATE_FILTERS,
  EUROPE_REGION_FILTER,
  type CandidateFilters,
} from '../dealroom/useCandidateFilters.js';
import {
  FilterDialogFrame,
  FilterGroup,
  FilterRow,
  StageFilter,
  stageFilterLabel,
  toggled,
  type FilterChip,
  stageFieldsOf,
} from '../ui/FilterDialog.js';
import { MultiSelect } from '../ui/MultiSelect.js';
import type { SelectOption } from '../ui/Select.js';
import { formatCount } from '../ui/format.js';
import { Switch } from '../ui/Switch.js';

const SECTOR_OPTIONS: readonly SelectOption[] = CLASSIFIABLE_SECTOR_TAGS.map((tag) => ({
  value: tag,
  label: tag,
}));

const REGION_OPTIONS: readonly SelectOption[] = CANDIDATE_REGION_FILTERS.map((region) => ({
  value: region,
  label: regionFilterLabel(region),
}));

const KNOW_OPTIONS: readonly SelectOption[] = KNOW_ANYONE_OPTIONS.map((option) => ({
  value: option.id,
  label: option.label,
}));

const CONTACT_SELECT_OPTIONS: readonly SelectOption[] = CONTACT_OPTIONS.map((option) => ({
  value: option.id,
  label: option.label,
}));

const SOURCE_SELECT_OPTIONS: readonly SelectOption[] = SOURCE_OPTIONS.map((option) => ({
  value: option.id,
  label: option.label,
}));

const TYPE_OPTIONS: readonly SelectOption[] = CANDIDATE_TYPE_OPTIONS.map((option) => ({
  value: option.id,
  label: option.label,
}));

function regionFilterLabel(region: string): string {
  return region === EUROPE_REGION_FILTER ? 'Europe (any)' : region;
}

function without<T>(values: readonly T[], value: T): T[] {
  return values.filter((candidate) => candidate !== value);
}

function sameTypes(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((type) => right.includes(type));
}

/**
 * Every active value, in the dialog's order, each removable on its own. The
 * noise controls (rounds seen, type) count only where they differ from the
 * defaults, and removing one puts the default back. The search box and the
 * order are not here: both are always in view.
 */
export function candidateFilterChips(filters: CandidateFilters): FilterChip<CandidateFilters>[] {
  const chips: FilterChip<CandidateFilters>[] = [];
  for (const stage of stageFilterChipStages({ mode: filters.stageMode, stages: filters.stages })) {
    chips.push({
      key: `stage:${stage ?? 'any'}`,
      label: stageFilterLabel(filters.stageMode, stage),
      remove: (f) => {
        const next = removeStageChip({ mode: f.stageMode, stages: f.stages }, stage);
        return { ...f, stageMode: next.mode, stages: [...next.stages] };
      },
    });
  }
  for (const sector of filters.sectors) {
    chips.push({
      key: `sector:${sector}`,
      label: sector,
      remove: (f) => ({ ...f, sectors: without(f.sectors, sector) }),
    });
  }
  for (const region of filters.regions) {
    chips.push({
      key: `region:${region}`,
      label: `Deals in ${regionFilterLabel(region)}`,
      remove: (f) => ({ ...f, regions: without(f.regions, region) }),
    });
  }
  if (filters.minRounds !== DEFAULT_CANDIDATE_FILTERS.minRounds) {
    chips.push({
      key: 'minRounds',
      label: `Seen in ${filters.minRounds}+ rounds`,
      remove: (f) => ({ ...f, minRounds: DEFAULT_CANDIDATE_FILTERS.minRounds }),
    });
  }
  if (!sameTypes(filters.types, DEFAULT_CANDIDATE_FILTERS.types)) {
    chips.push({
      key: 'types',
      label: sameTypes(filters.types, ALL_CANDIDATE_TYPES)
        ? 'Every type'
        : CANDIDATE_TYPE_OPTIONS.filter((option) => filters.types.includes(option.id))
            .map((option) => option.label)
            .join(' · '),
      remove: (f) => ({ ...f, types: DEFAULT_CANDIDATE_FILTERS.types }),
    });
  }
  for (const code of filters.hqCountries) {
    chips.push({
      key: `hq:${code}`,
      label: `HQ ${countryName(code)}`,
      remove: (f) => ({ ...f, hqCountries: without(f.hqCountries, code) }),
    });
  }
  if (filters.europeOnly) {
    chips.push({
      key: 'europeOnly',
      label: 'HQ in Europe',
      remove: (f) => ({ ...f, europeOnly: false }),
    });
  }
  if (filters.coInvestedOnly) {
    chips.push({
      key: 'coInvestedOnly',
      label: 'Has overlap',
      remove: (f) => ({ ...f, coInvestedOnly: false }),
    });
  }
  for (const value of filters.knowAnyone) {
    const label = KNOW_ANYONE_OPTIONS.find((option) => option.id === value)?.label ?? value;
    chips.push({
      key: `knowAnyone:${value}`,
      label: `Know anyone: ${label}`,
      remove: (f) => ({ ...f, knowAnyone: without(f.knowAnyone, value) }),
    });
  }
  for (const value of filters.contact) {
    const label = CONTACT_OPTIONS.find((option) => option.id === value)?.label ?? value;
    chips.push({
      key: `contact:${value}`,
      label: `Contact: ${label}`,
      remove: (f) => ({ ...f, contact: without(f.contact, value) }),
    });
  }
  for (const value of filters.sources) {
    const label = SOURCE_OPTIONS.find((option) => option.id === value)?.label ?? value;
    chips.push({
      key: `source:${value}`,
      label: `Source: ${label}`,
      remove: (f) => ({ ...f, sources: without(f.sources, value) }),
    });
  }
  return chips;
}

/** The stage filter as the candidates query asks it. */
export function stageQuery(filters: CandidateFilters): { leadsAt?: Stage[]; investsAt?: Stage[] } {
  const wants = stageFilterWants({ mode: filters.stageMode, stages: filters.stages });
  if (wants === null) return {};
  return wants.kind === 'leads' ? { leadsAt: [...wants.stages] } : { investsAt: [...wants.stages] };
}

/** Everything but the search and the order back to where a first visit starts. */
export function clearedCandidateFilters(filters: CandidateFilters): CandidateFilters {
  return { ...DEFAULT_CANDIDATE_FILTERS, text: filters.text, sort: filters.sort };
}

export interface CandidateFilterDialogProps {
  filters: CandidateFilters;
  onChange: (update: (filters: CandidateFilters) => CandidateFilters) => void;
  /** Where the candidates in view are based, most common first. */
  countries: readonly CandidateCountry[];
  /** How many the filters keep right now; null while it is being counted. */
  matching: number | null;
  onClose: () => void;
}

/**
 * The funds grid's Filters dialog, asked of the Dealroom candidates: what their
 * own rounds say (stages, sectors and regions of the companies they backed),
 * what kind of firm they are and where, and whether they have sat beside us.
 */
export function CandidateFilterDialog(props: CandidateFilterDialogProps): React.JSX.Element {
  const { filters, onChange } = props;
  const set = (change: Partial<CandidateFilters>): void =>
    onChange((current) => ({ ...current, ...change }));

  const countryOptions: readonly SelectOption[] = useMemo(() => {
    const label = (code: string, count: number | null): string =>
      [flagEmoji(code), countryName(code), count === null ? '' : `(${count})`]
        .filter((part) => part !== '')
        .join(' ');
    const offered = props.countries.map((country) => ({
      value: country.code,
      label: label(country.code, country.count),
    }));
    // A country somebody chose that has since emptied out stays, so it can
    // still be unticked.
    const chosenOnly = filters.hqCountries
      .filter((code) => !props.countries.some((country) => country.code === code))
      .map((code) => ({ value: code, label: label(code, null) }));
    return [...offered, ...chosenOnly];
  }, [props.countries, filters.hqCountries]);

  return (
    <FilterDialogFrame
      canClear={candidateFilterChips(filters).length > 0}
      onClear={() => onChange(clearedCandidateFilters)}
      onClose={props.onClose}
      showLabel={
        props.matching === null
          ? 'Show candidates'
          : `Show ${formatCount(props.matching)} candidate${props.matching === 1 ? '' : 's'}`
      }
    >
      <FilterGroup title="Activity · their rounds, last 36 months" icon={Activity}>
        <StageFilter
          mode={filters.stageMode}
          stages={filters.stages}
          onChange={(change) =>
            set(
              stageFieldsOf(
                changeStageFilter({ mode: filters.stageMode, stages: filters.stages }, change),
              ),
            )
          }
        />
        <FilterRow label="Sectors">
          <MultiSelect
            label="Sector"
            noun="sectors"
            placeholder="Every sector"
            options={SECTOR_OPTIONS}
            selected={filters.sectors}
            onToggle={(value) => {
              const chosen = toggled(filters.sectors, value, SECTOR_OPTIONS);
              set({
                sectors: CLASSIFIABLE_SECTOR_TAGS.filter((tag: SectorTag) => chosen.includes(tag)),
              });
            }}
            onClear={() => set({ sectors: [] })}
          />
        </FilterRow>
        <FilterRow label="Deals in">
          <MultiSelect
            label="Regions of their deals"
            noun="regions"
            placeholder="Any region"
            options={REGION_OPTIONS}
            selected={filters.regions}
            onToggle={(value) => {
              const chosen = toggled(filters.regions, value, REGION_OPTIONS);
              set({
                regions: CANDIDATE_REGION_FILTERS.filter((region) => chosen.includes(region)),
              });
            }}
            onClear={() => set({ regions: [] })}
          />
        </FilterRow>
        <FilterRow label="Rounds seen">
          <label className="min-rounds">
            at least
            <input
              type="number"
              min={0}
              value={filters.minRounds}
              onChange={(event) => set({ minRounds: Number(event.target.value) })}
            />
          </label>
        </FilterRow>
      </FilterGroup>

      <FilterGroup title="Profile" icon={Landmark}>
        <FilterRow label="Type">
          <MultiSelect
            label="Type"
            noun="types"
            placeholder="Every type"
            options={TYPE_OPTIONS}
            selected={filters.types}
            onToggle={(value) => {
              const chosen = toggled(filters.types, value, TYPE_OPTIONS);
              set({ types: ALL_CANDIDATE_TYPES.filter((type) => chosen.includes(type)) });
            }}
            onClear={() => set({ types: ALL_CANDIDATE_TYPES })}
          />
        </FilterRow>
        <FilterRow label="HQ">
          <MultiSelect
            label="HQ country"
            noun="countries"
            placeholder="Anywhere"
            options={countryOptions}
            selected={filters.hqCountries}
            onToggle={(value) =>
              set({ hqCountries: toggled(filters.hqCountries, value, countryOptions) })
            }
            onClear={() => set({ hqCountries: [] })}
          />
          <Switch checked={filters.europeOnly} onChange={(europeOnly) => set({ europeOnly })}>
            Europe only
          </Switch>
        </FilterRow>
      </FilterGroup>

      <FilterGroup title="Relationship" icon={Users}>
        <FilterRow label="Overlap">
          <Switch
            checked={filters.coInvestedOnly}
            onChange={(coInvestedOnly) => set({ coInvestedOnly })}
          >
            Has overlap
          </Switch>
        </FilterRow>
        <FilterRow label="Know anyone?">
          <MultiSelect
            label="Know anyone? (Affinity)"
            noun="answers"
            placeholder="Any answer"
            options={KNOW_OPTIONS}
            selected={filters.knowAnyone}
            onToggle={(value) => {
              const chosen = toggled(filters.knowAnyone, value, KNOW_OPTIONS);
              set({
                knowAnyone: KNOW_ANYONE_OPTIONS.map((option) => option.id).filter((id) =>
                  chosen.includes(id),
                ),
              });
            }}
            onClear={() => set({ knowAnyone: [] })}
          />
        </FilterRow>
        <FilterRow label="Last contact">
          <MultiSelect
            label="Last contact"
            noun="answers"
            placeholder="Any"
            options={CONTACT_SELECT_OPTIONS}
            selected={filters.contact}
            onToggle={(value) => {
              const chosen = toggled(filters.contact, value, CONTACT_SELECT_OPTIONS);
              set({
                contact: CONTACT_OPTIONS.map((option) => option.id).filter((id) =>
                  chosen.includes(id),
                ),
              });
            }}
            onClear={() => set({ contact: [] })}
          />
        </FilterRow>
        <FilterRow label="Source">
          <MultiSelect
            label="Where it came from"
            noun="sources"
            placeholder="Any source"
            options={SOURCE_SELECT_OPTIONS}
            selected={filters.sources}
            onToggle={(value) => {
              const chosen = toggled(filters.sources, value, SOURCE_SELECT_OPTIONS);
              set({
                sources: SOURCE_OPTIONS.map((option) => option.id).filter((id) =>
                  chosen.includes(id),
                ),
              });
            }}
            onClear={() => set({ sources: [] })}
          />
        </FilterRow>
      </FilterGroup>
    </FilterDialogFrame>
  );
}
